/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import { View } from 'backbone.marionette';
import Syphon from 'backbone.syphon';
import _ from 'lodash';

import formatAsDollars from '@/util/formatAsDollars';
import { validateForm } from '@/util/ParsleyUtils';
// import isNew from '../../../common/parsley/isNew';
import PhoneFormatBehavior from '@/behaviors/PhoneFormatBehavior';

import tmplDeliveryScheduleModalView from './DeliveryScheduleModalView.hbs';

const DeliveryScheduleModalView = View.extend({
  template: tmplDeliveryScheduleModalView,

  className: 'delivery-schedule-modal-view',

  ui: {
    nextBtn: '.nextBtn',
    prevBtn: '.prevBtn',
    copyBtn: '.copyBtn',
    saveBtn: '.saveBtn',
    deliveryScheduleForm: '.delivery-schedule-form',
    checkbox: '.form-check-input',
    roomNumber: '#roomNumber',
    poleNumber: '#poleNumber',
    suite: '#suite',
    itPOCSelect: '.itPOC-select',
    deliveryPOCSelect: '.deliveryPOC-select',
    finPOCSelect: '.finPOC-select',
    itPOCName: '.itPOCName',
    itPOCEmail: '.itPOCEmail',
    itPOCPhone: '.itPOCPhone',
    deliveryPOCName: '.deliveryPOCName',
    deliveryPOCEmail: '.deliveryPOCEmail',
    deliveryPOCPhone: '.deliveryPOCPhone',
    finPOCName: '.finPOCName',
    finPOCEmail: '.finPOCEmail',
    finPOCPhone: '.finPOCPhone',
    isReplacement: '.isReplacement-select',
    termingSerialNumber: '.terming-serial-number',
    additionalAccessories: '#additionalAccessories',
    additionalServices: '#additionalServices',
    accessoryPrice: '#accessoryPrice',
    servicePrice: '#servicePrice',
    regionType: '[name="regionTypeId"]',
    regionsDropdown: '#regions',
    regionsContainer: '#regionsContainer',
    regionsLabel: '#regionsContainer label'
  },

  events: {
    'click @ui.copyBtn': 'copyPrevious',
    'click @ui.nextBtn': 'clickNext',
    'click @ui.prevBtn': 'clickPrev',
    'click @ui.saveBtn': 'clickSave',
    'change @ui.checkbox': 'changeCheckbox',
    'change @ui.itPOCSelect': 'changeITPOC',
    'change @ui.deliveryPOCSelect': 'changeDeliveryPOC',
    'change @ui.finPOCSelect': 'changeFinPOC',
    'change @ui.isReplacement': 'changeIsReplacement',
    'change @ui.additionalAccessories': 'setAccessoryPrice',
    'change @ui.additionalServices': 'setServicePrice',
    'change @ui.regionType': 'setRegionOptions'
  },

  behaviors: {
    telephone: PhoneFormatBehavior
  },

  setRegionOptions() {
    const self = this;
    const regionType = self.ui.regionType.filter(':checked').val();
    const optionPlaceholder = $('<option>', {
      value: '',
      text: '--'
    });

    // If nothing is selected, hide dropdown and return
    if (!regionType) {
      self.ui.regionsContainer.hide();
      self.ui.regionsDropdown.empty();
      self.ui.regionsDropdown.append(optionPlaceholder);
      return;
    }

    // Show dropdown and populate options
    const regions =
      regionType === '1' ? self.model.get('conusOconusRegions').CONUS : self.model.get('conusOconusRegions').OCONUS;
    // Set label based on region type
    regionType === '1' ? this.ui.regionsLabel.text('State/Territory') : this.ui.regionsLabel.text('Country');

    self.ui.regionsContainer.show();
    self.ui.regionsDropdown.empty();
    self.ui.regionsDropdown.append(optionPlaceholder);

    // Get saved regionId if any
    const savedRegionId = self.model.get('address').regionId;

    _.each(regions, (region) => {
      self.ui.regionsDropdown.append(
        $('<option>', {
          value: region.id,
          text: region.name,
          selected: savedRegionId === region.id
        })
      );
    });
  },

  changeCheckbox(e) {
    const $checkbox = this.$(e.currentTarget);
    const correspondingInput = $checkbox.attr('corresponding-input');
    const disabled = !$checkbox.prop('checked');
    this.ui[correspondingInput].prop('disabled', disabled);
    if (disabled) {
      this.ui[correspondingInput].val('');
    }
  },

  changeITPOC(e) {
    e.preventDefault();
    const itPOCId = this.ui.itPOCSelect.val();
    const currPOC = this.model.get('commandPOCs').itPOCs.find((poc) => poc.id === itPOCId);

    if (itPOCId) {
      const itPOCName = `${currPOC.firstName} ${currPOC.lastName}`;
      const itPOCEmail = currPOC.email;
      const itPOCPhone = currPOC.telephone;
      this.ui.itPOCName.val(itPOCName);
      this.ui.itPOCEmail.val(itPOCEmail);
      this.ui.itPOCPhone.val(itPOCPhone);
    } else {
      this.ui.itPOCName.val('');
      this.ui.itPOCEmail.val('');
      this.ui.itPOCPhone.val('');
    }
  },

  changeDeliveryPOC(e) {
    e.preventDefault();
    const deliveryPOCId = this.ui.deliveryPOCSelect.val();
    const currPOC = this.model.get('commandPOCs').allPOCs.find((poc) => poc.id === deliveryPOCId);

    if (deliveryPOCId) {
      const deliveryPOCName = `${currPOC.firstName} ${currPOC.lastName}`;
      const deliveryPOCEmail = currPOC.email;
      const deliveryPOCPhone = currPOC.telephone;
      this.ui.deliveryPOCName.val(deliveryPOCName);
      this.ui.deliveryPOCEmail.val(deliveryPOCEmail);
      this.ui.deliveryPOCPhone.val(deliveryPOCPhone);
    } else {
      this.ui.deliveryPOCName.val('');
      this.ui.deliveryPOCEmail.val('');
      this.ui.deliveryPOCPhone.val('');
    }
  },

  changeFinPOC(e) {
    e.preventDefault();
    const finPOCId = this.ui.finPOCSelect.val();
    const currPOC = this.model.get('commandPOCs').allPOCs.find((poc) => poc.id === finPOCId);

    if (finPOCId) {
      const finPOCName = `${currPOC.firstName} ${currPOC.lastName}`;
      const finPOCEmail = currPOC.email;
      const finPOCPhone = currPOC.telephone;
      this.ui.finPOCName.val(finPOCName);
      this.ui.finPOCEmail.val(finPOCEmail);
      this.ui.finPOCPhone.val(finPOCPhone);
    } else {
      this.ui.finPOCName.val('');
      this.ui.finPOCEmail.val('');
      this.ui.finPOCPhone.val('');
    }
  },

  changeIsReplacement() {
    // e.preventDefault();
    const isReplacement = this.ui.isReplacement.val();
    if (isReplacement === '0') {
      this.ui.termingSerialNumber.val('New');
    } else {
      this.ui.termingSerialNumber.val('');
    }

    this.ui.deliveryScheduleForm.parsley().refresh();
  },

  toggleForm(state) {
    this.$el.find(':input').prop('disabled', !state);
  },

  templateContext() {
    const orderTypeID = this.model.get('orderTypeID');
    return {
      isReadOnly: this.options.isReadOnly,
      itPOCExists: this.options.itPOCExists,
      finPOCExists: this.options.finPOCExists,
      baseOrg: this.options.baseOrg,
      hasAdditionalServices: this.model.get('additionalServices')
        ? this.model.get('additionalServices').length > 0
        : null,
      hasAdditionalAccessories: this.model.get('additionalAccessories')
        ? this.model.get('additionalAccessories').length > 0
        : null,
      isRelocationOrder: this.options.isRelocationOrder,
      isAWPackage: this.options.objectTypeCode === 'AW_PACKAGE',
      isDesktop: orderTypeID === '20' || orderTypeID === '21' ? true : false,
      isOconus: orderTypeID == '21',
      isWide: orderTypeID == '5' || orderTypeID == '17',
      isDesktopOrWide:
        orderTypeID !== '20' && orderTypeID !== '21' && orderTypeID !== '5' && orderTypeID !== '17' ? false : true,
      isReplacement: this.model.get('isReplacement'),
      isPRTNRA3: orderTypeID == '13',
      conusRegions: this.model.get('conusOconusRegions').CONUS,
      oconusRegions: this.model.get('conusOconusRegions').OCONUS
    };
  },

  onRender() {
    this.ui.checkbox.each((i, checkbox) => {
      this.changeCheckbox({ currentTarget: checkbox });
    });

    if (this.options.isReadOnly) {
      this.toggleForm(false);
      this.ui.nextBtn.prop('disabled', false);
      this.ui.prevBtn.prop('disabled', false);
    }

    const curIndex = _.findIndex(this.model.collection.models, ['id', this.model.get('id')]);
    const maxIndex = this.model.collection.length - 1;
    if (curIndex === 0 || curIndex === -1) {
      this.ui.copyBtn.hide();
      this.ui.prevBtn.hide();
    }
    if (curIndex === maxIndex || curIndex === -1) {
      this.ui.nextBtn.hide();
    }

    // Handle regionType and regions dropdown initial state
    const address = this.model.get('address');
    let regionTypeVal;
    if (!!address.isConus) {
      regionTypeVal = address.isConus === '1' ? '1' : '0';
    } else {
      regionTypeVal = undefined;
    }
    // Set radio button if regionType is known
    if (typeof regionTypeVal !== 'undefined') {
      this.ui.regionType.filter(`[value="${regionTypeVal}"]`).prop('checked', true);
      this.setRegionOptions();
    } else {
      // No regionType selected, hide dropdown
      this.ui.regionType.prop('checked', false);
      this.ui.regionsContainer.hide();
      this.ui.regionsDropdown.empty();
      this.ui.regionsDropdown.append(
        $('<option>', {
          value: '',
          text: '--'
        })
      );
    }

    this.setAccessoryPrice();
    this.setServicePrice();
    this.ui.deliveryScheduleForm.parsley().refresh();
  },

  copyPrevious(e) {
    e.preventDefault();
    const curIndex = _.findIndex(this.model.collection.models, ['id', this.model.get('id')]);
    const prevIndex = curIndex - 1;
    const prevModel = this.model.collection.models[prevIndex];
    if (prevModel) {
      Syphon.deserialize(this, prevModel.toJSON());
      this.ui.checkbox.each((i, checkbox) => {
        this.changeCheckbox({ currentTarget: checkbox });
      });
      Syphon.deserialize(this, prevModel.toJSON());
    }
  },

  async clickNext() {
    await validateForm(this.ui.deliveryScheduleForm);
    const curIndex = _.findIndex(this.model.collection.models, ['id', this.model.get('id')]);
    const nextIndex = curIndex + 1;
    const formData = this.getFormData();
    this.triggerMethod('modal:close', { nextIndex, button: 'next', formData });
  },

  async clickPrev() {
    await validateForm(this.ui.deliveryScheduleForm);
    const curIndex = _.findIndex(this.model.collection.models, ['id', this.model.get('id')]);
    const nextIndex = curIndex - 1;
    const formData = this.getFormData();
    this.triggerMethod('modal:close', { nextIndex, button: 'prev', formData });
  },

  async clickSave() {
    await validateForm(this.ui.deliveryScheduleForm);
    const formData = this.getFormData();
    this.triggerMethod('modal:close', { button: 'save', formData });
  },

  getFormData() {
    const formData = Syphon.serialize(this);
    const address = formData.address;

    if (address) {
      address.buildingFloorNumber =
        address.buildingFloorNumber && !address.buildingFloorNumber.match(/^BLDG /)
          ? 'BLDG '.concat(address.buildingFloorNumber)
          : address.buildingFloorNumber;
      address.floorNumber =
        address.floorNumber && !address.floorNumber.match(/^FL /)
          ? 'FL '.concat(address.floorNumber)
          : address.floorNumber;
      address.roomNumber =
        address.roomNumber && !address.roomNumber.match(/^RM /) ? 'RM '.concat(address.roomNumber) : address.roomNumber;
      address.poleNumber =
        address.poleNumber && !address.poleNumber.match(/^POLE /)
          ? 'POLE '.concat(address.poleNumber)
          : address.poleNumber;
      address.suite = address.suite && !address.suite.match(/^STE /) ? 'STE '.concat(address.suite) : address.suite;

      address.roomPoleNumber = [address.roomNumber, address.poleNumber, address.suite].filter((x) => x).join(' ');

      address.isConus = formData.regionTypeId;
    }

    return formData;
  },

  async setAccessoryPrice() {
    const additionalAccessories = this.model.get('additionalAccessories');
    const accessoryId = this.ui.additionalAccessories.val();

    if (accessoryId) {
      const accessory = _.find(additionalAccessories, { id: accessoryId });
      this.ui.accessoryPrice.val(formatAsDollars(accessory.price));
    } else {
      this.ui.accessoryPrice.val('');
    }
  },

  async setServicePrice() {
    const additionalServices = this.model.get('additionalServices');
    const serviceId = this.ui.additionalServices.val();

    if (serviceId) {
      const service = _.find(additionalServices, { id: serviceId });
      this.ui.servicePrice.val(formatAsDollars(service.price));
    } else {
      this.ui.servicePrice.val('');
    }
  }
});

export default DeliveryScheduleModalView;
